import React, { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { usePbDispatch, usePbSelector } from '../../store/pbStoreHooks';
import {
    DeleteOperationalContact,
    GetOperationalContacts,
    OperationalContactDto,
    OperationalContactWriteDto,
    ResetDeleteOperationalContact,
    ResetPutOperationalContact,
    UpdateOperationalContact,
} from '../../store/operationalContactsSlice';
import {
    ManagedRequest,
    RequestStatus,
} from '../../store/network/managedRequest';
import EditLargeCardFormik from '../main/edit-large-card-formik';
import useTranslate from '../useTranslate';
import OperationalContactBasicForm, {
    filterOperationalContactValues,
    validateOperationalContact,
} from './operational-contact-basic-form';
import ConfirmButton from '../util/confirm-button';
import { goToOperationalContactOverview } from '../../nav/navigationService';
import { PbRequest } from '../../store/pbRequest';

interface Props {
    contactId: number;
    hasWritePermission: boolean;
}

const EMPTY_OPERATIONAL_CONTACT: OperationalContactWriteDto = {
    name: '',
    email: '',
    phone1: '',
    phone2: '',
};

function OperationalContactBasicCard(props: Readonly<Props>) {
    const strings = useTranslate('OperationalContactEdit');
    const [newInit, setNewInit] = React.useState(false);
    const dispatch = usePbDispatch();
    const countryIsoCode = usePbSelector(
        (state) =>
            state.countriesDetails.idToCountry[
                state.partnerManager.selectedPartner?.country_id ?? 1
            ]?.isocode1
    );
    const listRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.GET_OPERATIONAL_CONTACTS]
    );
    const listRequestData = usePbSelector(
        (state) => state.operationalContacts.operationalContacts
    );
    const updateRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.PUT_OPERATIONAL_CONTACT]
    );
    const deleteRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.DELETE_OPERATIONAL_CONTACT]
    );
    const isError = updateRequest.status === RequestStatus.FAILURE;

    useEffect(() => {
        return () => {
            resetEditError();
            dispatch(ResetDeleteOperationalContact());
        };
    }, [props.contactId]);
    useEffect(() => {
        if (listRequest.status === RequestStatus.INITIAL) {
            dispatch(GetOperationalContacts());
        }
    }, [listRequest.status]);
    useEffect(() => {
        if (listRequest.status === RequestStatus.PENDING) {
            setNewInit(true);
        } else if (newInit && listRequest.status === RequestStatus.SUCCESS) {
            setNewInit(false);
        }
    }, [newInit, listRequest.status]);

    const contactData = useMemo(() => {
        return listRequestData?.list?.find(
            (it) => it.operational_contact_id === props.contactId
        );
    }, [listRequestData?.list, props.contactId]);

    const initialValues: OperationalContactWriteDto =
        contactData ?? EMPTY_OPERATIONAL_CONTACT;

    const lastChangedNote =
        contactData?.mtime &&
        strings._utils.sprintf(
            strings.changedNote,
            new Date(contactData.mtime).toLocaleString()
        );

    return (
        <Row>
            <Col xs={8}>
                <EditLargeCardFormik
                    title={strings.title}
                    handleSubmit={handleSubmit}
                    initialValues={initialValues}
                    error={isError}
                    disabled={!props.hasWritePermission}
                    errorElement={getUpdateErrorMessage()}
                    footnote={lastChangedNote}
                    enableReinitialize={newInit}
                    validate={validateOperationalContact}
                    handleCancel={resetEditError}
                    validationEnabled
                >
                    {(formProps) => (
                        <>
                            <OperationalContactBasicForm
                                active={formProps.active}
                                disabled={!formProps.active}
                                defaultCountry={countryIsoCode}
                            />
                            {!formProps.active &&
                                props.hasWritePermission &&
                                renderDeleteButton()}
                        </>
                    )}
                </EditLargeCardFormik>
            </Col>
        </Row>
    );

    function renderDeleteButton() {
        return (
            <div>
                {getDeleteErrorMessage()}
                <ConfirmButton
                    danger
                    onConfirmedAction={deleteContact}
                    linkClassName="icon icon-wew-trash icon-sm"
                    confirmTitle={strings.deleteModal.title}
                    confirmMessage={strings.deleteModal.description}
                    confirmAction={strings.deleteModal.confirm}
                    rejectAction={strings.deleteModal.cancel}
                >
                    <span
                        className="btn btn-action"
                        style={{
                            marginLeft: '1em',
                        }}
                    >
                        {strings.delete}
                    </span>
                </ConfirmButton>
            </div>
        );
    }

    function deleteContact() {
        dispatch(
            DeleteOperationalContact(
                props.contactId,
                goToOperationalContactOverview
            )
        );
    }

    function resetEditError() {
        dispatch(ResetPutOperationalContact());
    }

    function handleSubmit(values: OperationalContactDto) {
        dispatch(
            UpdateOperationalContact(
                values.operational_contact_id,
                filterOperationalContactValues(values)
            )
        );
    }

    function getDeleteErrorMessage() {
        return getErrorMessage(deleteRequest);
    }

    function getUpdateErrorMessage() {
        return getErrorMessage(updateRequest);
    }

    function getErrorMessage(request: ManagedRequest<void>) {
        const className = 'alert-danger';
        const errors = request.errors;
        if (request.status === RequestStatus.FAILURE) {
            const knownErrors = errors.map((it) =>
                strings.errorTags[it] ? (
                    <div key={it} className={className}>
                        {strings.errorTags[it]}
                    </div>
                ) : null
            );
            return [
                <div key="UNKNOWN" className={className}>
                    {strings.errorTags.UNKNOWN}
                </div>,
                ...knownErrors,
            ];
        }
        return null;
    }
}

export default OperationalContactBasicCard;
